import {
	Box,
	Button,
	Divider,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	Spinner,
	Stack,
	Text,
	joinAttributes,
	Alert,
	useId,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { Markdown } from "@sembark-travel/ui-markdown"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { TBrandWiseColors, TTripQuoteEmailTemplate } from "./store"
import { isTruthy } from "@sembark-travel/ui-form"
import { FontFamily } from "./FontFamily"
import {
	EditTripQuoteEmailTemplateForm,
	TEmailPdfTemplateCreatePayload,
} from "./EditTripQuoteEmailTemplateForm"

export function TripQuoteEmailTemplates() {
	const xhr = useXHR()
	const { data, mutate, error } = useSWR("/trip-quote-email-templates", () =>
		xhr
			.get<{
				data: Array<TTripQuoteEmailTemplate>
				meta: TBrandWiseColors
			}>("/trip-quote-email-templates")
			.then((resp) => resp.data)
	)
	const { hasPermission } = useCheckPermissions()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	if (!data) {
		if (error) {
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}
	const dark_fill =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-700"]
	const borderColor =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-600"] ||
		"emphasis"
	const light_fill =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-200"]
	const textColor =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-800"]
	return (
		<Stack gap="4">
			<Inline justifyContent="between" gap="4" alignItems="center">
				<Inline gap="4" alignItems="center">
					<Inline
						size="12"
						rounded="full"
						bgColor="default"
						borderWidth="1"
						alignItems="center"
						justifyContent="center"
						color="muted"
					>
						<Icons.Mail size="6" />
					</Inline>
					<Stack gap="1">
						<Heading as="h3">Quote Email Template Configurations</Heading>
						<Text>
							These configuration will be applied to Quote Emails only.
						</Text>
					</Stack>
				</Inline>
				{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Box>
						<Button
							onClick={openDialog}
							level={!data.data.length ? "primary" : "secondary"}
							status="primary"
							size="sm"
						>
							<Icons.Pencil /> Edit
						</Button>
					</Box>
				) : null}
			</Inline>
			<Divider marginY="0" />
			<Box>
				{!data.data.length ? (
					<Box padding="4" bgColor="default" rounded="md" borderWidth="1">
						<Text>Using default quote email template configurations.</Text>
					</Box>
				) : (
					data.data.map((template) => (
						<Stack key={template.id} gap="4">
							<Text>
								<b>Font Family</b>: {template.settings.fontFamily.name}
							</Text>
							<Text>
								<b>Justified Text</b>:{" "}
								{isTruthy(template.settings.justifiedText)
									? "Justified"
									: "Default"}
							</Text>
							<Stack gap="1">
								<Text fontWeight="semibold">Subject:</Text>
								<Box
									paddingX="4"
									paddingY="2"
									borderWidth="1"
									rounded="md"
									bgColor="default"
								>
									{template.subject_line || "Using the default subject."}
								</Box>
							</Stack>
							<Stack gap="1">
								<Text fontWeight="semibold">Greetings:</Text>
								<FontFamily family={template.settings.fontFamily}>
									<Box
										style={{
											textAlign: isTruthy(template.settings.justifiedText)
												? "justify"
												: undefined,
										}}
										paddingX="4"
										paddingY="2"
										borderWidth="1"
										rounded="md"
										bgColor="default"
									>
										<Markdown>{template.greetings}</Markdown>
									</Box>
								</FontFamily>
							</Stack>
							<Stack gap="1">
								<Text fontWeight="semibold">
									<Icons.Bed /> Settings for Hotels Section
								</Text>
								<Box padding="4" rounded="md" bgColor="default" borderWidth="1">
									<Stack gap="4">
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Title
											</Text>
											<Text>
												{template.hotel_section_settings?.title || "N/A"}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Package Price Location
											</Text>
											<Text>
												{isTruthy(
													template.hotel_section_settings
														?.show_package_price_below_hotel
												)
													? "Package Price will be shown with optionwise hotels"
													: "Package Price will be shown in a separate Total Price section"}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Similar Word In Hotel Names
											</Text>
											<Text>
												{isTruthy(
													template.hotel_section_settings?.similar_word_in_hotel
												)
													? "Enabled - Will show (Similar) word in Hotel Names - Example -> Taj Hotel / Similar"
													: "Disabled - Will not show (Similar) word in Hotel Names"}
											</Text>
										</Stack>
									</Stack>
								</Box>
							</Stack>
							<Stack gap="1">
								<Text fontWeight="semibold">
									<Icons.Bus /> Settings for Transportation and Activity Section
								</Text>
								<Box padding="4" rounded="md" bgColor="default" borderWidth="1">
									<Stack gap="4">
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Variant
											</Text>
											<Text>
												{template.transport_and_activity_section_settings
													.title || "Using default settings"}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Variant
											</Text>
											<Text>
												{
													template.transport_and_activity_section_settings
														.variant
												}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Hide Transport Summary if Same Cab Type is used?
											</Text>
											<Text>
												{isTruthy(
													template.transport_and_activity_section_settings
														.hide_transport_summary_for_same_cabs
												)
													? "Yes, Hide Transport Summary"
													: "No, Show Day Wise Transport Summary"}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Specific Terms and Notes
											</Text>
											<Markdown>
												{template.transport_and_activity_section_settings
													.terms || "N/A"}
											</Markdown>
										</Stack>
									</Stack>
								</Box>
							</Stack>
							{template.section_title_settings ? (
								<Stack gap="1">
									<Text fontWeight="semibold">Section Title Settings</Text>
									<Box
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="2">
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Settings
												</Text>
												<Text>
													{joinAttributes(
														`Background: ${template.section_title_settings.background}`,
														`Border: ${template.section_title_settings.border}`,
														`Text Align: ${template.section_title_settings.textAlign}`
													)}
												</Text>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Preview
												</Text>
												<Box
													fontSize="sm"
													paddingX="4"
													paddingY="2"
													fontWeight="semibold"
													style={{
														borderColor: borderColor,
														color:
															template.section_title_settings.background ===
															"dark_fill"
																? "#fff"
																: textColor,
														backgroundColor:
															template.section_title_settings.background ===
															"fill"
																? light_fill
																: template.section_title_settings.background ===
																	  "dark_fill"
																	? dark_fill
																	: "#fff",
													}}
													borderLeftWidth={
														template.section_title_settings.border === "left" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"vertical"
															? "4"
															: "0"
													}
													borderRightWidth={
														template.section_title_settings.border ===
															"right" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"vertical"
															? "4"
															: "0"
													}
													borderTopWidth={
														template.section_title_settings.border === "top" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"horizontal"
															? "4"
															: "0"
													}
													borderBottomWidth={
														template.section_title_settings.border ===
															"bottom" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"horizontal"
															? "4"
															: "0"
													}
													textAlign={
														template.section_title_settings.textAlign !== "auto"
															? template.section_title_settings.textAlign
															: undefined
													}
												>
													Sample Title Text
												</Box>
											</Stack>
										</Stack>
									</Box>
								</Stack>
							) : null}
							{template.section_ordering_settings ? (
								<Stack gap="1">
									<Text fontWeight="semibold">
										<Icons.SwitchHorizontal rotate="90" /> Show, Hide & Reorder
										Email Sections
									</Text>
									<Stack
										gap="4"
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="2">
											{template.section_ordering_settings.map(
												(section, index) =>
													section.hide === 0 && (
														<Inline gap="2" alignItems={"center"} key={index}>
															<Stack
																size={"6"}
																alignItems="center"
																justifyContent="center"
																borderWidth="1"
																rounded="full"
																bgColor="success_emphasis"
																color="on_emphasis"
															>
																<Text fontWeight="semibold">{index + 1}</Text>
															</Stack>
															<Box flex="1" minWidth="0">
																<Box
																	paddingX={"4"}
																	paddingY={"2"}
																	borderWidth="1"
																	rounded="md"
																	maxWidth={"xs"}
																>
																	<Inline
																		gap="4"
																		alignItems={"center"}
																		justifyContent="between"
																	>
																		<Text>{section.title}</Text>
																	</Inline>
																</Box>
															</Box>
														</Inline>
													)
											)}
										</Stack>
									</Stack>
								</Stack>
							) : null}
							<Stack gap="1">
								<Text fontWeight="semibold">
									<Icons.SwitchHorizontal rotate="90" /> Day Wise Itinerary
									Settings
								</Text>
								<Stack
									gap="4"
									padding="4"
									rounded="md"
									bgColor="default"
									borderWidth="1"
								>
									<Stack gap="px">
										<Text fontSize="sm" fontWeight="semibold" color="muted">
											Section Title
										</Text>
										<Text>
											{template.daywise_schedule_section_settings?.title ||
												"N/A"}
										</Text>
									</Stack>
									<Stack gap="px">
										<Text fontSize="sm" fontWeight="semibold" color="muted">
											Travel Distance
										</Text>
										<Text>
											{isTruthy(
												template.daywise_schedule_section_settings
													?.hide_travel_distance
											)
												? "Always Hide"
												: "Always Show"}
										</Text>
									</Stack>
									<Stack gap="px">
										<Text fontSize="sm" fontWeight="semibold" color="muted">
											Show Services Together
										</Text>
										<Text>
											{isTruthy(
												template.daywise_schedule_section_settings
													?.show_same_day_service_together
											)
												? "Enabled - Will show services together in the same day"
												: "Disabled - Will not show services together in the same day"}
										</Text>
									</Stack>
								</Stack>
							</Stack>
							{template.visa_section_settings ? (
								<Stack gap="1">
									<Text fontWeight="semibold">
										<Icons.Chat /> Visa Settings
									</Text>
									<Stack
										gap="4"
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Title
											</Text>
											<Text>
												{template.visa_section_settings?.title ?? "N/A"}
											</Text>
										</Stack>
									</Stack>
								</Stack>
							) : null}
							<Stack gap="1">
								<Text fontWeight="semibold">
									<Icons.Chat /> Important Notes
								</Text>
								<Stack
									gap="4"
									padding="4"
									rounded="md"
									bgColor="default"
									borderWidth="1"
								>
									<Stack gap="px">
										<Text fontSize="sm" fontWeight="semibold" color="muted">
											Section Title
										</Text>
										<Text>{template.other_info_section_settings?.title}</Text>
									</Stack>
								</Stack>
							</Stack>
							<Stack gap="1">
								<Text fontWeight="semibold">
									<Icons.Support rotate="90" /> Terms Settings
								</Text>
								<Stack
									gap="4"
									padding="4"
									rounded="md"
									bgColor="default"
									borderWidth="1"
								>
									<Stack gap="px">
										<Text fontSize="sm" fontWeight="semibold" color="muted">
											Section Title
										</Text>
										<Text>{template.terms_section_settings?.title}</Text>
									</Stack>
								</Stack>
							</Stack>
							<Text color="muted" fontSize="sm">
								{joinAttributes(
									template.updated_by && template.updated_at ? (
										<>
											Updated by {template.updated_by.name},{" "}
											<RelativeTime timestamp={template.updated_at} />
										</>
									) : undefined,
									<>
										Created by {template.created_by?.name || "You"},{" "}
										<RelativeTime timestamp={template.created_at} />
									</>
								)}
							</Text>
						</Stack>
					))
				)}
			</Box>
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Update Trip Quote Email Template"
				lg
			>
				<Dialog.Body>
					<EditTripQuoteEmailTemplate
						onCancel={() => {
							closeDialog()
						}}
						onSuccess={() => {
							closeDialog()
							mutate()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</Stack>
	)
}
function EditTripQuoteEmailTemplate({
	onCancel,
	onSuccess,
}: {
	onCancel: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const id__ = useId()
	const { data } = useSWR(`/trip-quote-email-template/update?${id__}`, () =>
		xhr
			.get<{
				data: TEmailPdfTemplateCreatePayload
				meta: TBrandWiseColors
			}>("/trip-quote-email-templates/create")
			.then((resp) => resp.data)
	)
	if (!data) {
		return <Spinner alignCenter padding="4" />
	}
	return (
		<EditTripQuoteEmailTemplateForm
			initialValues={data.data}
			tenantColor={data.meta}
			onCancel={onCancel}
			onSubmit={async (payload) => {
				const data = {
					...payload,
					section_ordering_settings: (
						payload.section_ordering_settings || []
					).map((section, index) => {
						return {
							section: section.section,
							order: index + 1,
							hide: section.hide,
						}
					}),
				}
				await xhr.post("/trip-quote-email-templates", data)
				onSuccess()
			}}
		/>
	)
}
